import { useCallback, useEffect, forwardRef } from 'react';
import './styles.scss';

const Jodit = ({
  id = 'jodit-editor',
  name = 'jodit-editor-container',
  className = 'jodit-editor',
  editorRef = {},
  value = '',
  extras = {},
  onChange: handleChange,
}) => {
  useEffect(() => () => {
    const editorElement = document.getElementById(id);
    if (editorElement) {
      editorElement.removeEventListener('change', handleChange);
    }
  }, []);

  useEffect(() => {
    const eLs = document.querySelectorAll(`.${className}`);
    if (eLs.length !== 0) {
      return () => {};
    }

    const editor = window.Jodit.make(`#${id}`, {
      value,
      width: '100%',
      height: '100%',
      className,
      toolbarButtonSize: 'middle',
      toolbarAdaptive: true,
      statusbar: true,
      colorPickerDefaultTab: 'color',
      removeButtons: ['symbols', 'ai-commands', 'ai-assistant', 'classSpan', 'spellcheck'],
      usePopupForSpecialCharacters: false,
    });

    // set the ref
    const _refMap = new Map(editorRef.current);
    _refMap.set(name, editor);
    editorRef.current = _refMap;

    const editorElement = document.getElementById(id);
    if (editorElement) {
      editorElement.removeEventListener('change', handleChange);
      editorElement.addEventListener('change', handleChange);
    }

    return () => {};
  }, [handleChange]);

  return (
    <textarea
      id={id}
      name={name}
      className='d-none'
    />
  );
};

export const pickEditor = ({
  name = '',
  editorCollection = new Map([
    ['', { text: '', value: '' }],
  ]),
}) => {
  const _refMap = new Map(editorCollection);
  if (!_refMap.has(name)) {
    return { text: '', value: '' };
  }
  return _refMap.get(name) || { text: '', value: '' };
};

export const pickState = ({
  name = '',
  editor = { text: '', value: '' },
  editorCollection = new Map([
    ['', { text: '', value: '' }],
  ]),
}) => {
  const editorState = {
    text: editor?.text,
    value: editor?.value,
  };
  const updatedState = new Map();
  const _refMap = new Map(editorCollection);
  _refMap.forEach((editor, name) => {
    const editorState = {
      text: editor?.text,
      value: editor?.value,
    };
    updatedState.set(name, { name, editorState });
  });
  updatedState.set(name, { name, editorState });

  return { name, editorState, updatedState, editor };
};

export const pickPureText = ({ text = ''}) => {
  const editorText = text?.replaceAll(/\n/gm, '')?.trim() || '';
  if (!editorText.length) {
    return '';
  }

  return editorText;
};

export const pickEditorState = ({
  name = '',
  state = new Map(),
}) => {
  const { editorState } = state.get(name) || { editorState: { text: '', value: '' } };
  if (typeof editorState?.text === 'string') {
    editorState.text = pickPureText({ text: editorState.text });
  }

  return editorState;
};

export const Editor = forwardRef(({
  id = '',
  name = '',
  className = '',
  editorRef = {},
  value = '',
  extras = {},
  onChange: callChange = () => {},
}, _ref) => {
  const handleChange = useCallback((_event) => {
    const editor = pickEditor({ name, editorCollection: editorRef.current });
    callChange(pickState({ name, editor, editorCollection: editorRef.current }));
  }, [name, editorRef, callChange]);

  return (
    <Jodit
      id={id}
      name={name}
      className={className}
      editorRef={editorRef}
      value={value}
      extras={extras}
      onChange={handleChange}
    />
  );
});

export default Editor;
